<script setup lang="ts">
import { computed } from "vue";

import { navigationActions } from "@/action/navigationActions";
import ActionSource from "@/components/action/ActionSource.vue";
import LinkedItems from "@/components/link/LinkedItems.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { LinkingObjective } from "@/model/link";
import { useObjectiveStore } from "@/store/objective";

defineOptions({ inheritAttrs: false });

const props = defineProps<{ linkingObjective: LinkingObjective }>();

const linksCount = computed(() => {
  const objective = useObjectiveStore().objectiveById(props.linkingObjective.id, {
    boardId: props.linkingObjective.boardId,
  });
  return objective ? objective.cards.filter((card) => card.isOrigin).length : 0;
});

const handleAddLinks = () => {
  return navigationActions.openModal("attribute-chip", "LinkModal", {
    attrs: {
      targets: [props.linkingObjective],
      setReturnFocus: "#objective-links-chip-trigger-" + props.linkingObjective.id,
    },
  });
};
</script>

<template>
  <ActionSource value="attribute-chip">
    <DropdownMenu
      :id="'objective-links-chip-trigger-' + linkingObjective.id"
      :width="linksCount ? 'xlg' : 'md'"
      max-height="lg"
      stay-open-on-content-click
      @click.stop
      @keydown.enter.stop
    >
      <template #trigger>
        <div class="trigger">
          <BaseTooltip position="bottom">
            <button
              :class="['objective-links-attribute-chip', { active: linksCount > 0 }, $attrs.class]"
              data-dropdown-trigger
            >
              <span class="icon">
                <SvgIcon name="general/link" />
              </span>
              <span class="text">
                {{ linksCount }}
              </span>
            </button>
            <template #content>
              {{ $t("general.links") }}
            </template>
          </BaseTooltip>
        </div>
      </template>
      <LinkedItems :linking-target="linkingObjective" @add="handleAddLinks" />
    </DropdownMenu>
  </ActionSource>
</template>

<style scoped lang="scss">
@use "@/styles/variables/a11y" as colors-a11y;
@use "@/styles/variables/colors";
@use "@/styles/font";
@use "@/styles/board";

@mixin board-scale {
  padding: board.len(3px) board.len(4px);
  border: board.len(1px) dashed currentcolor;
  border-radius: board.len(4px);
  height: board.len(20px);

  &.active {
    background-color: colors.$background-grey;
  }

  > * + * {
    margin-left: board.len(4px);
  }

  .text {
    font-size: board.len(14px);
  }

  .icon {
    height: board.len(14px);
    width: board.len(14px);
  }
}

@mixin fixed-scale {
  padding: 3px 4px;
  border: 1px dashed currentcolor;
  border-radius: 4px;

  > * + * {
    margin-left: 4px;
  }

  .text {
    font-size: 14px;
  }

  .icon {
    height: 14px;
    width: 14px;
  }
}

.trigger {
  :deep(.base-tooltip) {
    display: block;
  }

  .objective-links-attribute-chip {
    display: flex;
    align-items: center;
    user-select: none;
    color: colors-a11y.$outline-black;
    cursor: pointer;
    outline: revert;

    &.board-scale {
      @include board-scale;
    }

    &.fixed-scale {
      @include fixed-scale;
    }

    .text {
      font-weight: font.$weight-normal;
    }

    .icon {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-shrink: 0;
    }

    &:hover:not(.active, :active) {
      color: colors.$palette-black-alpha-60;
    }

    &.active {
      border: none;
      color: colors.$text-subtle;

      &:hover,
      &:active {
        background-color: colors.$palette-black-alpha-10;
      }
    }
  }
}
</style>
